import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/infra/ui/components/ui/dialog';
import { Button } from '@/shared/infra/ui/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/infra/ui/components/ui/select';
import {
  accountSelectionSchema,
  AccountSelectionFormValues,
} from '../../../application/validators/account-selection.schema';
import { useAccountSelectionViewModel } from '../view-models/useAccountSelectionViewModel';

interface AccountSelectionDialogProps {
  open: boolean;
  mode: 'mandatory' | 'switchable';
  defaultSelectedAccountId?: string;
  onClose?: () => void;
}

export const AccountSelectionDialog: React.FC<AccountSelectionDialogProps> = ({
  open,
  mode,
  defaultSelectedAccountId,
  onClose,
}) => {
  const { accountList, isLoadingAccounts, selectAccount, isSelectingAccount } =
    useAccountSelectionViewModel({ onClose });

  const form = useForm<AccountSelectionFormValues>({
    resolver: zodResolver(accountSelectionSchema),
    defaultValues: {
      accountId: defaultSelectedAccountId || '',
    },
    mode: 'onBlur',
  });

  // Reset form when default selection changes
  useEffect(() => {
    if (defaultSelectedAccountId) {
      form.setValue('accountId', defaultSelectedAccountId);
    }
  }, [defaultSelectedAccountId, form]);

  const handleValidSubmit = (values: AccountSelectionFormValues) => {
    selectAccount(values.accountId);
  };

  const handleCancel = () => {
    if (mode === 'switchable' && onClose) {
      form.reset();
      onClose();
    }
  };

  // Prevent closing in mandatory mode
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && mode === 'mandatory') {
      return; // Do not close
    }
    if (!newOpen && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`max-w-2xl ${mode === 'mandatory' ? '[&>button]:hidden' : ''}`}>
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {mode === 'mandatory' ? 'Select Account' : 'Switch Account'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleValidSubmit)} noValidate className="space-y-4 py-2">
          <p className="text-sm text-gray-600">
            {mode === 'mandatory'
              ? 'Please select an account to continue.'
              : 'Choose an account to switch to.'}
          </p>

          {/* Account Select Dropdown */}
          <Controller
            name="accountId"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="space-y-1">
                <label htmlFor="org-select" className="text-sm font-medium text-gray-900">
                  Account <span className="text-red-500">*</span>
                </label>
                <Select
                  value={field.value}
                  onValueChange={(val) => field.onChange(val)}
                  disabled={isLoadingAccounts || isSelectingAccount}
                >
                  <SelectTrigger id="org-select" className="w-full mt-1">
                    <SelectValue placeholder="Select an account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accountList.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldState.error && (
                  <p className="text-sm text-red-500 mt-1" role="alert">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            {mode === 'switchable' && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancel}
                className="text-gray-600 text-sm"
                disabled={isSelectingAccount}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 text-white text-sm px-6"
              disabled={isLoadingAccounts || isSelectingAccount}
            >
              {isSelectingAccount ? 'Selecting...' : 'Confirm'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
